import {MouseEvent, useLayoutEffect, useState} from "react";
import {css} from "@panda/css";

import {ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {
    IPricesHubTableHoverMetrics,
    PRICES_HUB_TABLE_HOVER_OVERFLOW_X_PX,
    PRICES_HUB_TABLE_HOVER_OVERFLOW_Y_PX
} from "../../hooks/use_prices_hub_table_row_hover";
import {trackPricesHubPlaceClick} from "../../tracking/track_prices_hub_place_click";
import {formatPriceByUnit, formatSignedPercent} from "../../utils/format_price_m2";
import {formatRcnPeriodLabel} from "../../utils/format_rcn_period_label";
import {getCityLink} from "../../utils/get_city_link";
import {ITransactionPriceRow} from "../../utils/get_transaction_price_rows";
import {PricesHubCityIcon} from "./PricesHubCityIcon";
import {PricesHubTrendChip} from "./PricesHubTrendChip";

interface IProps {
    row: ITransactionPriceRow;
    dominantPeriodLabel: string;
    metrics: IPricesHubTableHoverMetrics;
    isVisible: boolean;
    onMouseMove: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export const PricesHubTransactionPricesTableHoverRow = (props: IProps) => {
    const {row, dominantPeriodLabel, metrics, isVisible, onMouseMove} = props;
    const [cityWidth = 0, offerWidth = 0, periodOfferWidth = 0, transactionWidth = 0, chipWidth = 0, actionWidth = 0] = metrics.columns;
    const [opacity, setOpacity] = useState(0);

    useLayoutEffect(() => {
        if (!isVisible) {
            setOpacity(0);
            return;
        }

        const timeout = window.setTimeout(() => setOpacity(1), 0);

        return () => window.clearTimeout(timeout);
    }, [isVisible]);

    return (
        <a
            href={getCityLink(row.slug)}
            tabIndex={-1}
            aria-label={`Zobacz ceny mieszkań – ${row.name}`}
            className={overlayStyle}
            style={{
                top: metrics.top - PRICES_HUB_TABLE_HOVER_OVERFLOW_Y_PX,
                height: metrics.height + PRICES_HUB_TABLE_HOVER_OVERFLOW_Y_PX * 2,
                opacity,
                pointerEvents: isVisible ? "auto" : "none",
                gridTemplateColumns: `${cityWidth + PRICES_HUB_TABLE_HOVER_OVERFLOW_X_PX}px ${offerWidth}px ${periodOfferWidth}px ${transactionWidth}px ${
                    chipWidth + PRICES_HUB_TABLE_HOVER_OVERFLOW_X_PX
                }px ${actionWidth}px`
            }}
            onMouseMove={onMouseMove}
            onClick={() => trackPricesHubPlaceClick(row)}
        >
            <div className={cityCellStyle}>
                <PricesHubCityIcon slug={row.slug} size="2.8" tone="light" />
                <Text as="span" variant="body_copy_1">
                    {row.name}
                </Text>
            </div>
            <Text as="span" variant="body_copy_1" className={numberCellStyle}>
                {formatPriceByUnit(row.offerPriceM2, row.unit)}
            </Text>
            <span className={dividedCellStyle}>
                <Text as="span" variant="body_copy_1">
                    {row.periodOfferPriceM2 === null ? "—" : formatPriceByUnit(row.periodOfferPriceM2, row.unit)}
                </Text>
                {row.periodLabel === dominantPeriodLabel ? null : (
                    <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                        {formatRcnPeriodLabel(row.periodLabel)}
                    </Text>
                )}
            </span>
            <span className={transactionCellStyle}>
                <Text as="span" variant="body_copy_1">
                    {formatPriceByUnit(row.transactionPriceM2, row.unit)}
                </Text>
                {row.periodLabel === dominantPeriodLabel ? null : (
                    <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                        {formatRcnPeriodLabel(row.periodLabel)}
                    </Text>
                )}
            </span>
            <div className={chipCellStyle}>
                <PricesHubTrendChip size="large" isBelow={row.differencePct <= 0}>
                    {formatSignedPercent(row.differencePct)}
                </PricesHubTrendChip>
            </div>
            <div className={actionCellStyle}>
                <ChevronRightIcon size="2.2" fill="currentColor" />
            </div>
        </a>
    );
};

const overlayStyle = css({
    position: "absolute",
    left: 0,
    zIndex: 2,
    display: "grid",
    alignItems: "center",
    width: "100%",
    cursor: "pointer",
    color: "var(--colors-white)",
    textDecoration: "none",
    backgroundColor: "var(--colors-secondary)",
    borderRadius: "1.4rem",
    boxShadow: "0 2px 6px 0 rgba(35, 35, 45, 0.12), 0 14px 32px -6px rgba(35, 35, 45, 0.3)",
    transition: "opacity 220ms ease, top 70ms ease-out, height 70ms ease-out",

    _hover: {
        color: "var(--colors-white)",
        textDecoration: "none"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    },

    "@media (prefers-reduced-motion: reduce)": {
        transition: "none"
    }
});

const cityCellStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    minWidth: 0,
    pl: "2.6rem"
});

const numberCellStyle = css({
    px: 1.5,
    textAlign: "right",
    color: "rgba(255, 255, 255, 0.92)",
    whiteSpace: "nowrap"
});

const dividedCellStyle = css({
    px: 1.5,
    textAlign: "right",
    color: "rgba(255, 255, 255, 0.92)",
    borderLeft: "1px solid rgba(255, 255, 255, 0.24)",
    whiteSpace: "nowrap"
});

const transactionCellStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    px: 1.5,
    color: "rgba(255, 255, 255, 0.92)",
    whiteSpace: "nowrap"
});

const periodNoteStyle = css({
    display: "block",
    opacity: 0.7
});

const chipCellStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    px: 1.5
});

const actionCellStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    pr: "2.2rem"
});
import {MouseEvent, useLayoutEffect, useState} from "react";
import {css} from "@panda/css";

import {ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {
    IPricesHubTableHoverMetrics,
    PRICES_HUB_TABLE_HOVER_OVERFLOW_X_PX,
    PRICES_HUB_TABLE_HOVER_OVERFLOW_Y_PX
} from "../../hooks/use_prices_hub_table_row_hover";
import {trackPricesHubPlaceClick} from "../../tracking/track_prices_hub_place_click";
import {formatPriceByUnit, formatSignedPercent} from "../../utils/format_price_m2";
import {formatRcnPeriodLabel} from "../../utils/format_rcn_period_label";
import {getCityLink} from "../../utils/get_city_link";
import {ITransactionPriceRow} from "../../utils/get_transaction_price_rows";
import {PricesHubCityIcon} from "./PricesHubCityIcon";
import {PricesHubTrendChip} from "./PricesHubTrendChip";

interface IProps {
    row: ITransactionPriceRow;
    dominantPeriodLabel: string;
    metrics: IPricesHubTableHoverMetrics;
    isVisible: boolean;
    onMouseMove: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export const PricesHubTransactionPricesTableHoverRow = (props: IProps) => {
    const {row, dominantPeriodLabel, metrics, isVisible, onMouseMove} = props;
    const [cityWidth = 0, offerWidth = 0, periodOfferWidth = 0, transactionWidth = 0, chipWidth = 0, actionWidth = 0] = metrics.columns;
    const [opacity, setOpacity] = useState(0);

    useLayoutEffect(() => {
        if (!isVisible) {
            setOpacity(0);
            return;
        }

        const timeout = window.setTimeout(() => setOpacity(1), 0);

        return () => window.clearTimeout(timeout);
    }, [isVisible]);

    return (
        <a
            href={getCityLink(row.slug)}
            tabIndex={-1}
            aria-label={`Zobacz ceny mieszkań – ${row.name}`}
            className={overlayStyle}
            style={{
                top: metrics.top - PRICES_HUB_TABLE_HOVER_OVERFLOW_Y_PX,
                height: metrics.height + PRICES_HUB_TABLE_HOVER_OVERFLOW_Y_PX * 2,
                opacity,
                pointerEvents: isVisible ? "auto" : "none",
                gridTemplateColumns: `${cityWidth + PRICES_HUB_TABLE_HOVER_OVERFLOW_X_PX}px ${offerWidth}px ${periodOfferWidth}px ${transactionWidth}px ${
                    chipWidth + PRICES_HUB_TABLE_HOVER_OVERFLOW_X_PX
                }px ${actionWidth}px`
            }}
            onMouseMove={onMouseMove}
            onClick={() => trackPricesHubPlaceClick(row)}
        >
            <div className={cityCellStyle}>
                <PricesHubCityIcon slug={row.slug} size="2.8" tone="light" />
                <Text as="span" variant="body_copy_1">
                    {row.name}
                </Text>
            </div>
            <Text as="span" variant="body_copy_1" className={numberCellStyle}>
                {formatPriceByUnit(row.offerPriceM2, row.unit)}
            </Text>
            <span className={dividedCellStyle}>
                <Text as="span" variant="body_copy_1">
                    {row.periodOfferPriceM2 === null ? "—" : formatPriceByUnit(row.periodOfferPriceM2, row.unit)}
                </Text>
                {row.periodLabel === dominantPeriodLabel ? null : (
                    <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                        {formatRcnPeriodLabel(row.periodLabel)}
                    </Text>
                )}
            </span>
            <span className={transactionCellStyle}>
                <Text as="span" variant="body_copy_1">
                    {formatPriceByUnit(row.transactionPriceM2, row.unit)}
                </Text>
                {row.periodLabel === dominantPeriodLabel ? null : (
                    <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                        {formatRcnPeriodLabel(row.periodLabel)}
                    </Text>
                )}
            </span>
            <div className={chipCellStyle}>
                <PricesHubTrendChip size="large" isBelow={row.differencePct <= 0}>
                    {formatSignedPercent(row.differencePct)}
                </PricesHubTrendChip>
            </div>
            <div className={actionCellStyle}>
                <ChevronRightIcon size="2.2" fill="currentColor" />
            </div>
        </a>
    );
};

const overlayStyle = css({
    position: "absolute",
    left: 0,
    zIndex: 2,
    display: "grid",
    alignItems: "center",
    width: "100%",
    cursor: "pointer",
    color: "var(--colors-white)",
    textDecoration: "none",
    backgroundColor: "var(--colors-secondary)",
    borderRadius: "1.4rem",
    boxShadow: "0 2px 6px 0 rgba(35, 35, 45, 0.12), 0 14px 32px -6px rgba(35, 35, 45, 0.3)",
    transition: "opacity 220ms ease, top 70ms ease-out, height 70ms ease-out",

    _hover: {
        color: "var(--colors-white)",
        textDecoration: "none"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    },

    "@media (prefers-reduced-motion: reduce)": {
        transition: "none"
    }
});

const cityCellStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    minWidth: 0,
    pl: "2.6rem"
});

const numberCellStyle = css({
    px: 1.5,
    textAlign: "right",
    color: "rgba(255, 255, 255, 0.92)",
    whiteSpace: "nowrap"
});

const dividedCellStyle = css({
    px: 1.5,
    textAlign: "right",
    color: "rgba(255, 255, 255, 0.92)",
    borderLeft: "1px solid rgba(255, 255, 255, 0.24)",
    whiteSpace: "nowrap"
});

const transactionCellStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    px: 1.5,
    color: "rgba(255, 255, 255, 0.92)",
    whiteSpace: "nowrap"
});

const periodNoteStyle = css({
    display: "block",
    opacity: 0.7
});

const chipCellStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    px: 1.5
});

const actionCellStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    pr: "2.2rem"
});
